/**
 * The in-app PDF viewer's mount point and byte cache — the PDF twin of
 * `SpreadsheetViewerContext`, and intentionally the same shape so a citation
 * handler can ask both in turn and fall back to open-in-tab when neither
 * claims the href.
 *
 * The cache is owned here rather than in the modal so it survives open/close:
 * clicking three citations into the same credit agreement fetches it once.
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

import PdfViewer, { LoadPdf, PdfCacheEntry } from "../components/PdfViewer";
import { downloadFile } from "../services/fileApi";
import { FileType } from "../types/fileType";
import { ParsedDopplerUri, parseDopplerUri } from "../utils/dopplerUri";

// Bound the byte cache: a credit agreement is tens of megabytes and we hold the
// raw buffer, so keep only the most-recently-used handful.
const PDF_CACHE_MAX = 3;

interface OpenArgs {
	parsed: ParsedDopplerUri;
	chatId?: string | null;
	filename?: string;
}

interface PdfViewerContextValue {
	/** Open the in-app viewer for a parsed ``doppler://pdf`` URI. */
	openPdf: (args: OpenArgs) => void;
	/** Convenience: parse a raw ``doppler://`` href and open it. Returns
	 * ``false`` if the href is not a viewable PDF URI, so the caller can fall
	 * back to the default open-in-tab behaviour. */
	openPdfHref: (
		href: string,
		opts?: { chatId?: string | null; filename?: string },
	) => boolean;
	/** The shared byte cache, for a surface that renders a page inline (the
	 * deal-screening source panel, DOP-617) instead of opening the modal. */
	loadPdf: LoadPdf;
}

const PdfViewerContext = createContext<PdfViewerContextValue | null>(null);

export const PdfViewerProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [open, setOpen] = useState<OpenArgs | null>(null);

	// Promise cache keyed by file identity — NOT by signed URL (those rotate)
	// and NOT by page or quote (those are view state).
	const cacheRef = useRef<Map<string, PdfCacheEntry>>(new Map());

	const loadPdf = useCallback<LoadPdf>((parsed) => {
		const key = `${parsed.kind}:${parsed.subkind}:${parsed.target}`;
		const cache = cacheRef.current;
		const existing = cache.get(key);
		if (existing) {
			// Refresh LRU recency.
			cache.delete(key);
			cache.set(key, existing);
			return existing;
		}
		let resolvedName: string | undefined;
		const promise = (async () => {
			// Fetch bytes through the backend proxy rather than a GCS signed URL:
			// the request stays same-origin (already CORS-allowed for the UI
			// origin), so per-tenant buckets don't each need browser CORS. Same
			// reasoning — and the same endpoint — as the workbook loader.
			const fileId = Number(parsed.target);
			if (!Number.isInteger(fileId) || fileId <= 0) {
				throw new Error("Invalid PDF file id.");
			}
			const dl = await downloadFile(fileId);
			resolvedName = dl.filename ?? undefined;
			return dl.bytes;
		})();
		const entry: PdfCacheEntry = { promise };
		promise.then(
			(bytes) => {
				entry.value = bytes;
				entry.filename = resolvedName;
			},
			() => {
				// Evict failed promises so a transient error doesn't poison the file.
				// Identity-guard so a retry that re-took the key isn't dropped.
				if (cache.get(key) === entry) cache.delete(key);
			},
		);
		cache.set(key, entry);
		while (cache.size > PDF_CACHE_MAX) {
			const oldest = cache.keys().next().value;
			if (oldest === undefined) break;
			cache.delete(oldest);
		}
		return entry;
	}, []);

	const openPdf = useCallback((args: OpenArgs) => {
		setOpen(args);
	}, []);

	const openPdfHref = useCallback(
		(href: string, opts?: { chatId?: string | null; filename?: string }) => {
			const parsed = parseDopplerUri(href);
			if (!parsed || parsed.kind !== FileType.PDF) return false;
			setOpen({
				parsed,
				chatId: opts?.chatId,
				// Prefer an explicitly-passed filename; otherwise use the one the URI
				// carries (``&file=``) so the header names the document.
				filename: opts?.filename ?? parsed.filename,
			});
			return true;
		},
		[],
	);

	const value = useMemo(
		() => ({ openPdf, openPdfHref, loadPdf }),
		[openPdf, openPdfHref, loadPdf],
	);

	return (
		<PdfViewerContext.Provider value={value}>
			{children}
			{open && (
				<PdfViewer
					// Remount on a new target so page/pager state starts clean, but
					// reuse the mount when the same file is re-cited.
					key={`${open.parsed.target}:${open.parsed.page ?? 1}:${
						open.parsed.highlight ?? ""
					}:${open.parsed.number?.value ?? ""}:${
						open.parsed.number?.column ?? ""
					}`}
					parsed={open.parsed}
					chatId={open.chatId}
					filename={open.filename}
					loadPdf={loadPdf}
					onClose={() => setOpen(null)}
				/>
			)}
		</PdfViewerContext.Provider>
	);
};

/** Access the viewer opener. Returns ``null`` outside a provider so callers can
 * degrade gracefully to open-in-tab. */
export const usePdfViewer = (): PdfViewerContextValue | null =>
	useContext(PdfViewerContext);
